/* eslint-disable max-lines */

import React, { useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { Moment } from 'moment'

import { Tabs } from '@mui/material'

import AssignedSystemTime from 'pages/HehmDevice/AssignedSystemTime'
import HehmDashboard from 'pages/HehmDevice/HehmDashboard'
import Alerts from 'pages/HehmDevice/Alerts'

import KeyboardAccessible from 'components/KeyboardAccessible'
import Spinner from 'components/Spinner'

import ConfigurationAndComponentsTabContent from 'pages/HehmDevice/HehmDashboard/ConfigurationAndComponentsTabContent'
import SystemConditionTabContent from 'pages/HehmDevice/HehmDashboard/SystemConditionTabContent'
import ThermostatRthPanel from 'pages/Device/ThermostatRthPanel'

import useHehmDevice from 'hooks/useHehmDevice'
import useHehmDeviceEquipment from 'hooks/useHehmDeviceEquipment'
import useHehmDeviceHardware from 'hooks/useHehmDeviceHardware'
import useSearchParams from 'hooks/useSearchParams'
import NotesTabContent from 'pages/HehmDevice/HehmDashboard/HehmNotesPanel'
import { useSessionContext } from 'hooks/SessionContext'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'
import useDevice from 'hooks/useDevice'

type Props = {
  deviceId: string
  dealerId: number
  refreshPageCallback?: () => void | null
}

const HehmDevice = ({ deviceId, dealerId, refreshPageCallback }: Props) => {
  const navigate = useNavigate()

  const { activeTab } = useParams()

  const [selectedTab, setSelectedTab] = useState(activeTab || 'dashboard')
  const [thermostatRthDate, setThermostatRthDate] = useState(useSearchParams('thermostatRthDate'))

  const rthRef = useRef<HTMLDivElement>(null)

  const { device, isDeviceLoaded } = useDevice(dealerId, deviceId)
  const { hehmDevice, isHehmDeviceLoaded } = useHehmDevice(deviceId)
  const { hehmDeviceEquipment, isHehmDeviceEquipmentLoaded } = useHehmDeviceEquipment(deviceId)
  const { hehmDeviceHardware, isHehmDeviceHardwareLoaded } = useHehmDeviceHardware(deviceId)
  const { session } = useSessionContext()

  const handleTabNavigation = (selectedTab: string) => {
    const currentPath = location.pathname?.replace(/\/+$/, '')
    let newPath = currentPath
    if (selectedTab && selectedTab !== activeTab) {
      if (activeTab) {
        newPath = currentPath.replace(new RegExp(`/${activeTab}$`), `/${selectedTab}`)
      } else {
        newPath = `${currentPath}/${selectedTab}`
      }
      setSelectedTab(selectedTab)
    }

    const TAB_PARAMS: Record<string, string[]> = {
      'runtime-history': ['thermostatRthDate']
    }
    const SHARED_PARAMS = ['deviceIds']

    const params = new URLSearchParams(location.search)
    const preservedParams = new URLSearchParams()
    const allowedParams = [...(TAB_PARAMS[selectedTab] || []), ...SHARED_PARAMS]
    allowedParams.forEach(param => {
      const value = params.get(param)
      if (value) {
        preservedParams.set(param, value)
      }
    })

    const preservedSearch = preservedParams.toString()
    if (preservedSearch) {
      newPath += `?${preservedSearch}`
    }

    navigate(newPath, { replace: true })
  }

  const showDayRthCallback = (time: Moment) => {
    rthRef.current?.scrollIntoView()
    setThermostatRthDate(time.format('MM-DD-YYYY'))
    setSelectedTab('runtime-history')

    navigate({
      pathname: activeTab ? location.pathname.replace(new RegExp(`/${activeTab}$`), '/runtime-history') : location.pathname + '/runtime-history',
      search: `?thermostatRthDate=${time.format('MM-DD-YYYY')}`
    })
  }

  const renderHehmDashboardIcon = () => {
    return (
      <S.DashboardIcon
        src="/img/customize.svg"
        aria-hidden="true"
        data-testid="hehm-dashboard-icon"
      />
    )
  }

  if (!isHehmDeviceLoaded || !isHehmDeviceEquipmentLoaded || !isHehmDeviceHardwareLoaded || !isDeviceLoaded) {
    return <Spinner variant="page" />
  }

  if (device) {
    const getTabIndex = (tab: string) => {
      const index = tabs.findIndex(item => item.link === tab)

      return index !== -1 ? index : 0
    }

    const tabs = [
      {
        label: 'EHMKT Dashboard',
        link: 'dashboard',
        toolTipText: 'Click to see Dashboard overview.',
        content: <HehmDashboard
                 device={device}
                 hehmDevice={hehmDevice}
                 hehmDeviceEquipment={hehmDeviceEquipment}
                 hehmDeviceHardware={hehmDeviceHardware}
                 onNavigate={handleTabNavigation}
                 />
      },
      {
        label: 'System Condition',
        link: 'system-condition',
        toolTipText: 'Click to see System Condition details.',
        content: <SystemConditionTabContent {...{ hehmDevice }} />
      }, {
        label: 'Alerts',
        link: 'alerts',
        toolTipText: 'Click to see Alerts details.',
        content: <Alerts showDayRthCallback={showDayRthCallback} deviceTimeZone={device.timeZone} />
      },
      {
        label: 'Runtime History',
        link: 'runtime-history',
        toolTipText: 'Click to see Runtime History.',
        content: <ThermostatRthPanel
                 isActive={selectedTab === 'runtime-history'}
                 isHehm
                 device={{ deviceId: device.deviceId, dealerId: device.dealerId, deviceType: 'hehm' }}
                 timeZone={device.timeZone}
                 thermostatRthDate={thermostatRthDate}
                 updateThermostatRthDateCallback={setThermostatRthDate}
                 />
      },
      {
        label: 'Config/Components',
        link: 'config-components',
        toolTipText: 'Click to see Configuration and Components details.',
        content: <ConfigurationAndComponentsTabContent
                  {...{ hehmDevice, hehmDeviceEquipment, hehmDeviceHardware, readOnly: session.isFsr || session.isTechnician, deviceId }}
                  refreshPageCallback={refreshPageCallback}
                 />
      },
      {
        label: 'Notes',
        link: 'notes',
        toolTipText: 'Click to see Notes details.',
        enabled: !session.isFsr,
        content: <NotesTabContent
                 device={device}
                 />
      }
    ].filter(tab => tab.enabled !== false)

    return (
      <S.Container>
        <S.TabButtons
        variant="scrollable"
        value={getTabIndex(selectedTab)}
        aria-label="device tabs"
        scrollButtons="auto"
        allowScrollButtonsMobile
        TabIndicatorProps={{ sx: { display: 'none' } }}
        >
          {tabs.map((tab) => (
            <S.TabButton
            key={tab.link}
            onClick={() => { handleTabNavigation(tab.link) }}
            isActive={selectedTab === tab.link}
            >
              {tab.link === 'dashboard' && renderHehmDashboardIcon()}
              <S.TabLabel isActive={selectedTab === tab.link}>
                {tab.label}
              </S.TabLabel>
            </S.TabButton>
          ))}
        </S.TabButtons>

        <S.Content>
          {tabs.find(tab => tab.link === selectedTab)?.content}
        </S.Content>

        <AssignedSystemTime
        device={device}
        refreshPageCallback={refreshPageCallback}
        />
      </S.Container>
    )
  }
}

export default HehmDevice

const S = {
  Container: styled.div`
    min-height: 55vh;
    background-color: ${color('paneBackground')};
  `,
  Content: styled.div`
    padding: 20px;
     @media ${screenSize.medium} {
      padding: 10px;
    }
  `,
  TabButtons: styled(Tabs)`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    flex-direction: row;
    position: sticky;
    z-index: 99;
    top: 0;
    background-color: ${color('paneBackground')};
    align-items: center;
    padding: 0.5rem 1rem;
    @media ${screenSize.medium} {
      flex-wrap: nowrap;
      padding: 0.5rem 0rem;
    }
    & .MuiTabs-flexContainer {
      display: flex;
      flex-wrap: wrap;
      width: 100%;
      flex-direction: row;
      justify-content: flex-start;
      gap: 1.5rem;
      @media ${screenSize.medium} {
        flex-wrap: nowrap;
      }
    }
    & .MuiTabs-scrollButtons svg{
      font-size: 2.5rem;
    }
  `,
  TabButton: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['isActive'].includes(prop)
  }) <{ isActive: boolean }>`
    position: relative;
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ isActive }) => (isActive ? color('activeTabHighlight') : color('modalOverlay'))};
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    transition: border-bottom 0.4s ease-in-out;
    border-bottom: ${({ isActive }) =>
    `4px solid ${color(isActive ? 'activeTabHighlight' : 'paneBackground')}`};
  `,
  TabLabel: styled.span.withConfig({
    shouldForwardProp: (prop) => !['isActive'].includes(prop)
  }) <{ isActive: boolean }>`
    position: relative;
    display: inline-block;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    font-weight: ${({ isActive }) => (isActive ? '500' : '600')};
    text-transform: capitalize;
    min-height: 30px;
    line-height: 30px;
  ${({ isActive }) => isActive && css`&::after { width: 100%; }`}
  `,
  DashboardIcon: styled.img`
    width: 24px;
    height: 24px;
  `
}
